import { gql } from "@apollo/client";
import { businessUnitUser } from "api/User/fragment";

export const businessUnitFragment = gql`
  fragment BUFragment on BusinessUnit {
    id
    name
    profilePhoto
    warnings
    tbStatus
    postcode
    organisation {
      id
    }
    canCreateRfl
    tradingTypes {
      id
      slug
      name
    }
    businessType {
      id
      slug
    }
    locationsCount
    foggles
    regulatorySystem {
      id
      slug
      name
    }
    deliveryAddress {
      id
      unitName
      locationId
      postcode
      addressLine1
    }
    primaryLocation {
      id
      locationId
      addressLine1
      addressLine2
      city
      postcode
    }
    currency {
      id
      name
      code
    }
    unitsOfMeasurement {
      id
      code
      measurementType
    }
    country {
      id
      code
      name
      isoAlpha2
      isoNumber
      visualIdPrefix
      region {
        code
        name
      }
    }
    farmAnimalTypes {
      colour
      enabled
      id
      image
      name
      position
      slug
    }
    effectiveSubscriptionPlan {
      name
    }
    identifierPreferencesActiveIds {
      alternativeId
      passportNumber
      visualId
      eId
      name
      trichId
      tsuBarcode
      pedigreeId
      tattoo
      herdDoggId
      breedrId
      uhfId
      brucellosisId
      alternativeId
    }
    identifierPreferencesPrimaryId
    identifierPreferencesSecondaryId
    herdDoggIntegration {
      isConnected
    }
  }
`;

export const businessUnitTarget = gql`
  fragment businessUnitTarget on BusinessUnitTarget {
    id
    name
    details
    animalType {
      id
    }
  }
`;

export const GET_BUSINESS_UNITS_LIST = gql`
  query GetBusinessUnits {
    businessUnits {
      ...BUFragment
    }
  }
  ${businessUnitFragment}
`;

export const strippedBusinessUnit = gql`
  fragment BUStripped on BusinessUnit {
    id
    name
  }
`;

export const GET_STRIPPED_BUSINESS_UNITS_LIST = gql`
  query GetStrippedBusinessUnits {
    businessUnits {
      ...BUStripped
    }
  }
  ${strippedBusinessUnit}
`;

export const GET_BUSINESS_UNIT_BY_ID = gql`
  query GetBusinessUnit($businessUnit: Int!) {
    businessUnit(businessUnit: $businessUnit) {
      ...BUFragment
    }
  }
  ${businessUnitFragment}
`;

export const GET_BUSINESS_UNIT_USERS = gql`
  query GetBusinessUnitUsers($businessUnitId: Int) {
    businessUnitUsers(businessUnitId: $businessUnitId) {
      ...businessUnitUser
    }
  }
  ${businessUnitUser}
`;

export const GET_BUSINESS_UNIT_SETTINGS = gql`
  query GetBusinessUnitSettings($businessUnit: Int!) {
    businessUnit(businessUnit: $businessUnit) {
      id
      killOut
      enableBcmsPushTransactions
      tbStatus
      dateJoined
    }
  }
`;

export const GET_BUSINESS_UNIT_SIZE_UNIT = gql`
  query GetBusinessUnitSizeUnit($businessUnit: Int!) {
    businessUnit(businessUnit: $businessUnit) {
      id
      settings {
        sizeUnit
      }
    }
  }
`;

export const SEARCH_BU = gql`
  query SearchBusinessUnits(
    $query: String
    $roles: [String]
    $excludeBusinessUnitId: Int
    $preferredSuppliersBuyerId: Int
  ) {
    searchBusinessUnits(
      query: $query
      roles: $roles
      excludeBusinessUnitId: $excludeBusinessUnitId
      preferredSuppliersBuyerId: $preferredSuppliersBuyerId
    ) {
      id
      name
      location {
        city
        unitName
      }
    }
  }
`;

export const SEARCH_PREFERRED_BUSINESS_UNITS = gql`
  query SearchPreferredBusinessUnits(
    $query: String
    $roles: [String]
    $excludeBusinessUnitIds: [Int!]
    $preferredSuppliersBuyerId: Int
  ) {
    searchBusinessUnits(
      query: $query
      roles: $roles
      excludeBusinessUnitIds: $excludeBusinessUnitIds
      preferredSuppliersBuyerId: $preferredSuppliersBuyerId
    ) {
      id
      name
      location {
        city
        unitName
      }
    }
  }
`;

export const GET_PREFERRED_BUSINESS_UNITS = gql`
  query GetPreferredBusinessUnits($businessUnit: Int!) {
    preferredBusinessUnits(businessUnit: $businessUnit) {
      id
      supplier {
        id
        name
        primaryLocation {
          id
          city
          unitName
        }
      }
      buyer {
        id
        name
        primaryLocation {
          id
          city
          unitName
        }
      }
      fieldsman {
        id
        firstName
        lastName
      }
      priceAdjustment
      commonStatus
      tacTitle
      tacDescription
    }
  }
`;

export const GET_PREFERRED_BUSINESS_UNIT = gql`
  query GetPreferredBusinessUnit($businessUnit: Int!, $id: Int!) {
    preferredBusinessUnit(businessUnit: $businessUnit, id: $id) {
      id
      supplier {
        id
        name
      }
      fieldsman {
        id
      }
      priceAdjustment
    }
  }
`;

export const EMPTY_STATE = gql`
  query EmptyState($businessUnitId: Int!, $page: String!, $platform: String!, $animalType: Int!) {
    emptyState(businessUnitId: $businessUnitId, page: $page, platform: $platform, animalType: $animalType) {
      id
      text
      image
      page
      showButton
      button
      buttonLink
      buttonLabel
    }
  }
`;
